/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';

import { Box, Button, Typography, Dialog } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AttachmentsMultiSelect from './attachments/attachmentsMultiSelect.component';
import { 
  addRecurringPayableLineAttachment, 
  deleteRecurringPayableAttachment, 
  postRecurringPayableAttachment, 
  removeRecurringPayableLineAttachment, 
  updateRecurringPayableAttachment 
} from 'modules/expenses/recurringPayables/store/recurringPayables.action';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { 
  DELETE_RP_ATTACHMENT_FAILURE, 
  UPDATE_RP_ATTACHMENT_FAILURE, 
  POST_RP_LINE_ATTACHMENT_FAILURE, 
  DELETE_RP_LINE_ATTACHMENT_FAILURE 
} from 'modules/expenses/recurringPayables/store/recurringPayables.types';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';
import SpinnerComponent from './spinner.component';
import { showToast } from 'modules/layout/layout.actions';

const RecurringPayableAttachmentModal = props => {
  const { displayText, isReadOnly, displayButton, recurringPayableLineInfo, recurringPayableAttachments, recurringPayableLineAttachments, lineItemId } = props;
  const [open, setOpen] = useState(false);

  const [isUploading, setIsUploading] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file); 

      fileReader.onload = () => {
        resolve(fileReader.result);
      };

      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const saveFiles = async (filesToSave) => {
    setIsUploading(true);

    try {
      const allPromises = filesToSave.map(async (file) => {
        var base64 = await convertToBase64(file);

        base64 = base64.split(',')[1]; // Remove the data:<contentType>;base64, part

        const newRPAttachments = await props.postRecurringPayableAttachment({
          base64FileData: base64,
          fileName: file.name,
          contentType: file.type
        }, recurringPayableLineInfo.recurringPayableId);

        props.updateParentRPAttachments(newRPAttachments.response);
      });

      await Promise.all(allPromises);

      props.showToast('Successfully uploaded attachments', TOAST_MESSAGE_SEVERITY_SUCCESS);
    } catch (error) {
      console.error("Error uploading files:", error);
      props.showToast('Failed to upload attachments, please try again later', TOAST_MESSAGE_SEVERITY_ERROR);
    } finally {
      setIsUploading(false);
    }
  }

  const updateFile = async (file) => {
    setIsUploading(true);
    const updateFileResp = await props.updateRecurringPayableAttachment(file, recurringPayableLineInfo.recurringPayableId);
    if (updateFileResp.type === UPDATE_RP_ATTACHMENT_FAILURE) {
      props.showToast('Failed to update attachment, please try again later', TOAST_MESSAGE_SEVERITY_ERROR);
    } else {
      props.showToast('Successfully updated attachment', TOAST_MESSAGE_SEVERITY_SUCCESS);
      props.updateParentRPAttachments(updateFileResp.response);
    }
    setIsUploading(false);
  };

  const deleteFile = async (file) => {
    setIsUploading(true);
    const deleteFileResp = await props.deleteRecurringPayableAttachment(recurringPayableLineInfo.recurringPayableId, file.attachmentId);
    if (deleteFileResp.type === DELETE_RP_ATTACHMENT_FAILURE) {
      props.showToast('Failed to delete attachment, please try again later', TOAST_MESSAGE_SEVERITY_ERROR);
    } else {
      props.showToast('Successfully deleted attachment', TOAST_MESSAGE_SEVERITY_SUCCESS);
      props.updateParentRPAttachments(deleteFileResp.response);
    }
    setIsUploading(false);
  };

  const addLineAttachment = async (attachmentId) => {
    setIsUploading(true);
    const updatedLineAttachments = await props.addRecurringPayableLineAttachment(recurringPayableLineInfo.recurringPayableId, lineItemId, attachmentId);
    if (updatedLineAttachments.type === POST_RP_LINE_ATTACHMENT_FAILURE) {
      props.showToast('Failed to add line attachment, please try again later', TOAST_MESSAGE_SEVERITY_ERROR);
      return;
    } else {
      props.showToast('Successfully added line attachment', TOAST_MESSAGE_SEVERITY_SUCCESS);
      props.updateParentRPLineAttachments(updatedLineAttachments.response);
    }
    setIsUploading(false);
  }

  const removeLineAttachment = async (attachmentId) => {
    setIsUploading(true);
    const updatedLineAttachments = await props.removeRecurringPayableLineAttachment(recurringPayableLineInfo.recurringPayableId, lineItemId, attachmentId);
    if (updatedLineAttachments.type === DELETE_RP_LINE_ATTACHMENT_FAILURE) {
      props.showToast('Failed to remove line attachment, please try again later', TOAST_MESSAGE_SEVERITY_ERROR);
      return;
    } else {
      props.showToast('Successfully removed line attachment', TOAST_MESSAGE_SEVERITY_SUCCESS);
      props.updateParentRPLineAttachments(updatedLineAttachments.response);
    }
    setIsUploading(false);
  }

  return (
    <div>
      {!displayButton && (
        <>
          <Typography
            variant="body2"
            component="span"
            style={{ color: 'blue', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={handleOpen}
          >
            {displayText}
          </Typography>
        </>
      )}
      {displayButton && (
        <Button onClick={handleOpen} color="secondary" variant="contained" sx={{ maxHeight: '40px', marginLeft: '12px', marginTop: '8px' }}>
          Attachments
        </Button>
      )}
      <Dialog open={open} fullWidth={true} maxWidth={'lg'} onClose={handleClose}>
        <Box
          sx={{ padding: '20px' }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1 }}>
            <FontAwesomeIcon icon={['fas', 'file-contract']} size="2x" />
            <Typography id="attachment-modal-title" variant="h6" component="h2">
              <div>Line Item {lineItemId} Attachments</div>
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 1, mb: 2, color: 'text.secondary' }}>
            {!isReadOnly ? (
              <div>Select which attachments apply to the line item below or upload a new one. </div>
            ) : (
              <div>Images Selected</div>
            )}
          </Typography>

          {isUploading && <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
            <Typography variant="subtitle1">Updating Attachments...</Typography>
            <SpinnerComponent/>
          </Box>}

          <AttachmentsMultiSelect 
            saveFiles={saveFiles}
            fileAttachments={recurringPayableAttachments ? recurringPayableAttachments : []}
            lineAttachments={recurringPayableLineAttachments ? recurringPayableLineAttachments : []}
            isUploading={isUploading}
            updateFile={updateFile}
            deleteFile={deleteFile}
            addLineAttachment={addLineAttachment}
            removeLineAttachment={removeLineAttachment}
            isReadOnly={isReadOnly}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                handleClose();
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>
    </div>
  );
};

export default compose(connect(null, { 
  postRecurringPayableAttachment, 
  updateRecurringPayableAttachment, 
  deleteRecurringPayableAttachment, 
  addRecurringPayableLineAttachment, 
  removeRecurringPayableLineAttachment, 
  showToast 
}))(
  RecurringPayableAttachmentModal
);
